/**
 * Result pattern for better error handling and type safety
 * Inspired by Rust's Result type and functional programming patterns
 */

export type Success<T> = {
    success: true;
    data: T;
};

export type Failure<E = Error> = {
    success: false;
    error: E;
};

export type Result<T, E = Error> = Success<T> | Failure<E>;

/**
 * Create a successful result
 */
export function ok<T>(data: T): Success<T> {
    return { success: true, data };
}

/**
 * Create a failed result
 */
export function fail<E = Error>(error: E): Failure<E> {
    return { success: false, error };
}

/**
 * Check if a result is successful
 */
export function isOk<T, E>(result: Result<T, E>): result is Success<T> {
    return result.success === true;
}

/**
 * Check if a result is a failure
 */
export function isFail<T, E>(result: Result<T, E>): result is Failure<E> {
    return result.success === false;
}

/**
 * Map over a successful result
 */
export function mapResult<T, U, E>(result: Result<T, E>, fn: (data: T) => U): Result<U, E> {
    if (isOk(result)) {
        return ok(fn(result.data));
    }
    return result;
}

/**
 * Chain results together
 */
export function chainResult<T, U, E>(result: Result<T, E>, fn: (data: T) => Result<U, E>): Result<U, E> {
    if (isOk(result)) {
        return fn(result.data);
    }
    return result;
}

/**
 * Get the value or a default
 */
export function unwrapOr<T, E>(result: Result<T, E>, defaultValue: T): T {
    return isOk(result) ? result.data : defaultValue;
}

/**
 * Convert a throwing function to a Result-returning function
 */
export function tryCatch<T, E = Error>(fn: () => T, errorHandler?: (error: unknown) => E): Result<T, E> {
    try {
        return ok(fn());
    } catch (error) {
        const handledError = errorHandler ? errorHandler(error) : (error as E);
        return fail(handledError);
    }
}

/**
 * Combine multiple results into a single result
 * If any result is a failure, return the first failure
 */
export function combineResults<T, E>(results: Result<T, E>[]): Result<T[], E> {
    const values: T[] = [];

    for (const result of results) {
        if (isFail(result)) {
            return result;
        }
        values.push(result.data);
    }

    return ok(values);
}
